import { AUTH_FAILURE_CLASS, classifyAuthBootstrapFailure } from './authFailureClassification';
import {
  OFFLINE_SNAPSHOT_TYPES,
  normalizeOfflineMemberId,
} from './offlineConstants';
import {
  deleteOfflineSnapshot,
  getOfflineSnapshot,
  putOfflineSnapshot,
} from './offlineSnapshotStore';
import {
  OFFLINE_FRESHNESS,
  evaluateOfflineFreshness,
  isOfflineSnapshotRenderable,
} from './offlineFreshness';
import { classifyJourneyAccessError } from '../journey-workspace/journeyWorkspaceModel';

/**
 * Journey/Today request classification for cache fallback decisions.
 * Reuses Slice A auth classifier + Journey access codes.
 */
export function classifyJourneyRequestFailure(error) {
  const access = classifyJourneyAccessError(error);
  if (access === 'not-found' || access === 'forbidden') {
    return AUTH_FAILURE_CLASS.AUTHORITATIVE_DENIAL;
  }
  return classifyAuthBootstrapFailure(error);
}

export function mayUseJourneyOfflineFallback(error) {
  return classifyJourneyRequestFailure(error) === AUTH_FAILURE_CLASS.TRANSPORT_FAILURE;
}

export function isAuthoritativeJourneyDenial(error) {
  return classifyJourneyRequestFailure(error) === AUTH_FAILURE_CLASS.AUTHORITATIVE_DENIAL;
}

/**
 * AUTH58：刪除單一 member+Journey 的 OVERVIEW／TODAY 快照。
 * 不碰其他 Journey／其他 member／SW static cache。
 * 各 type 獨立刪除；單一失敗不阻斷另一個。
 */
export async function deleteJourneyOfflineSnapshots(memberId, journeyId) {
  const mid = normalizeOfflineMemberId(memberId);
  const jid = journeyId == null ? '' : String(journeyId).trim();
  const result = { overview: false, today: false };
  if (!mid || !jid) return result;

  try {
    await deleteOfflineSnapshot({
      memberId: mid,
      journeyId: jid,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.OVERVIEW,
    });
    result.overview = true;
  } catch {
    /* best-effort */
  }

  try {
    await deleteOfflineSnapshot({
      memberId: mid,
      journeyId: jid,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.TODAY,
    });
    result.today = true;
  } catch {
    /* best-effort */
  }

  return result;
}

/**
 * Authoritative denial 後 best-effort 清快照。
 * 永不拋出；呼叫端必須以 denial UI 為準，不得因清理失敗而 fallback。
 */
export async function clearJourneyOfflineSnapshotsAfterAuthoritativeDenial({
  memberId,
  journeyId,
} = {}) {
  try {
    return await deleteJourneyOfflineSnapshots(memberId, journeyId);
  } catch {
    return { overview: false, today: false };
  }
}

/**
 * AUTH59：Overview schedule warm 僅接受成功 live 物件；不寫入失敗／畸形結果。
 * 不 mutate 來源；不要求 schemaVersion 升級。
 */
export function isWarmableOverviewSchedule(scheduleRaw) {
  return scheduleRaw != null
    && typeof scheduleRaw === 'object'
    && !Array.isArray(scheduleRaw);
}

/**
 * Compose OVERVIEW payload：{ journey } 或 { journey, schedule }。
 * scheduleRaw === undefined → 保留先前 last-good schedule（若有）。
 * 無效／null scheduleRaw → 不覆寫 last-good；僅在無 prior 時省略 schedule。
 */
export function composeOverviewOfflinePayload({
  journeyRaw,
  scheduleRaw = undefined,
  previousPayload = null,
} = {}) {
  if (journeyRaw == null) return null;
  const prior = previousPayload && typeof previousPayload === 'object' ? previousPayload : {};
  const payload = { journey: journeyRaw };

  if (scheduleRaw !== undefined && isWarmableOverviewSchedule(scheduleRaw)) {
    payload.schedule = scheduleRaw;
    return payload;
  }

  if (isWarmableOverviewSchedule(prior.schedule)) {
    payload.schedule = prior.schedule;
  }
  return payload;
}

/**
 * Persist Overview last-good RAW journey（+ optional schedule）payload。
 * Failures are swallowed by callers — never break live UI.
 *
 * Progressive warm：
 * - detail 成功後可先寫 journey；若先前已有 schedule 則保留。
 * - schedule 成功後再以同一 journeyRaw + scheduleRaw 更新。
 * - schedule 失敗時呼叫端不應傳入失敗結果覆寫 last-good。
 */
export async function writeOverviewOfflineSnapshot({
  memberId,
  journeyId,
  journeyRaw,
  scheduleRaw = undefined,
}) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid || journeyRaw == null) return null;

  let previousPayload = null;
  try {
    const previous = await getOfflineSnapshot({
      memberId: mid,
      journeyId,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.OVERVIEW,
    });
    previousPayload = previous?.payload || null;
  } catch {
    previousPayload = null;
  }

  const payload = composeOverviewOfflinePayload({
    journeyRaw,
    scheduleRaw,
    previousPayload,
  });
  if (!payload) return null;

  return putOfflineSnapshot({
    memberId: mid,
    journeyId,
    snapshotType: OFFLINE_SNAPSHOT_TYPES.OVERVIEW,
    payload,
  });
}

export async function readOverviewOfflineSnapshot({ memberId, journeyId, nowMs = Date.now() }) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid) return null;
  let record;
  try {
    record = await getOfflineSnapshot({
      memberId: mid,
      journeyId,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.OVERVIEW,
    });
  } catch {
    return null;
  }
  if (!record?.payload?.journey) return null;
  const freshness = evaluateOfflineFreshness(record.fetchedAt, nowMs);
  if (!isOfflineSnapshotRenderable(record.fetchedAt, nowMs)) {
    return { record: null, freshness, reason: freshness };
  }
  return { record, freshness };
}

export async function hasUsableTodayOfflineSnapshot({ memberId, journeyId, nowMs = Date.now() }) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid) return false;
  try {
    const record = await getOfflineSnapshot({
      memberId: mid,
      journeyId,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.TODAY,
    });
    if (!record?.payload?.journey || !record?.payload?.schedule) return false;
    return isOfflineSnapshotRenderable(record.fetchedAt, nowMs);
  } catch {
    return false;
  }
}

/**
 * Build TODAY raw API bundle. Requires journey + schedule.
 * Logistics: use new values when fulfilled; else retain prior snapshot fields.
 */
export function composeTodayOfflinePayload({
  journeyRaw,
  scheduleRaw,
  staysRaw = undefined,
  transportsRaw = undefined,
  checklistRaw = undefined,
  decisionsRaw = undefined,
  situationsRaw = undefined,
  previousPayload = null,
}) {
  if (journeyRaw == null || scheduleRaw == null) return null;
  const prior = previousPayload && typeof previousPayload === 'object' ? previousPayload : {};
  const resolveSection = (next, priorValue) => {
    if (next !== undefined) return { value: next, status: 'ready' };
    if (priorValue !== undefined && priorValue !== null) {
      return { value: priorValue, status: 'retained' };
    }
    return { value: null, status: 'unavailable' };
  };
  const stays = resolveSection(staysRaw, prior.stays);
  const transports = resolveSection(transportsRaw, prior.transports);
  const checklist = resolveSection(checklistRaw, prior.checklist);
  const decisions = resolveSection(decisionsRaw, prior.decisions);
  const situations = resolveSection(situationsRaw, prior.situations);
  return {
    journey: journeyRaw,
    schedule: scheduleRaw,
    stays: stays.value,
    staysStatus: stays.status,
    transports: transports.value,
    transportsStatus: transports.status,
    checklist: checklist.value,
    checklistStatus: checklist.status,
    decisions: decisions.value,
    decisionsStatus: decisions.status,
    situations: situations.value,
    situationsStatus: situations.status,
  };
}

export async function writeTodayOfflineSnapshot({
  memberId,
  journeyId,
  journeyRaw,
  scheduleRaw,
  staysRaw,
  transportsRaw,
  checklistRaw,
  decisionsRaw,
  situationsRaw,
}) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid || journeyRaw == null || scheduleRaw == null) return null;

  let previousPayload = null;
  try {
    const previous = await getOfflineSnapshot({
      memberId: mid,
      journeyId,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.TODAY,
    });
    previousPayload = previous?.payload || null;
  } catch {
    previousPayload = null;
  }

  const payload = composeTodayOfflinePayload({
    journeyRaw,
    scheduleRaw,
    staysRaw,
    transportsRaw,
    checklistRaw,
    decisionsRaw,
    situationsRaw,
    previousPayload,
  });
  if (!payload) return null;

  return putOfflineSnapshot({
    memberId: mid,
    journeyId,
    snapshotType: OFFLINE_SNAPSHOT_TYPES.TODAY,
    payload,
  });
}

export async function readTodayOfflineSnapshot({ memberId, journeyId, nowMs = Date.now() }) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid) return null;
  let record;
  try {
    record = await getOfflineSnapshot({
      memberId: mid,
      journeyId,
      snapshotType: OFFLINE_SNAPSHOT_TYPES.TODAY,
    });
  } catch {
    return null;
  }
  if (!record?.payload?.journey || !record?.payload?.schedule) return null;
  const freshness = evaluateOfflineFreshness(record.fetchedAt, nowMs);
  if (!isOfflineSnapshotRenderable(record.fetchedAt, nowMs)) {
    return { record: null, freshness, reason: freshness };
  }
  return { record, freshness };
}

export { AUTH_FAILURE_CLASS, OFFLINE_FRESHNESS };
